import { ExternalLink, Share2 } from "lucide-react";
import { PortfolioDataItem } from "@/types/types";
import {
  getPortfolioBrandPath,
  getPortfolioMediaPath,
  sharePortfolioPath,
} from "@/lib/portfolioLinks";

interface WebsiteGalleryProps {
  group: PortfolioDataItem;
}

const WebsiteGallery: React.FC<WebsiteGalleryProps> = ({ group }) => {
  return (
    <section className="scroll-mt-28 pb-20">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-poppins text-sm uppercase tracking-[0.18em] text-[#c3f620]">
            {group.title}
          </p>
          <h2 className="mt-2 font-orbitron text-2xl font-medium uppercase tracking-[1px] lg:text-4xl">
            {group.brand}
          </h2>
          <p className="mt-3 max-w-3xl font-poppins text-sm leading-6 text-[#f5f5f5]/65">
            {group.description}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {group.visitUrl && group.visitUrl !== "#" && (
            <a
              href={group.visitUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#c3f620] px-4 py-2 font-poppins text-sm font-semibold text-[#0d1821] outline-none ring-[#f5f5f5] transition hover:bg-[#f5f5f5] focus-visible:ring-2"
            >
              Visit website
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          <button
            type="button"
            onClick={() =>
              sharePortfolioPath(
                `${group.brand} website portfolio`,
                getPortfolioBrandPath(group),
              )
            }
            className="inline-flex items-center gap-2 rounded-full border border-[#00a878]/40 px-4 py-2 font-poppins text-sm text-[#f5f5f5] outline-none ring-[#00a878] transition hover:bg-[#00a878] focus-visible:ring-2"
          >
            Share
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid gap-8">
        {group.media.map((src, index) => {
          const mobileSrc =
            group.mobileMedia?.[index] || group.mobileMedia?.[0];

          return (
            <article
              key={`${src}-${index}`}
              className="rounded-lg border border-[#f5f5f5]/10 bg-[#f5f5f5]/5 p-3 lg:p-5"
            >
              <div className="mb-4 flex items-center justify-between gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#00a878]">
                  Website view {String(index + 1).padStart(2, "0")}
                </p>
                <button
                  type="button"
                  onClick={() =>
                    sharePortfolioPath(
                      `${group.brand} website view ${index + 1}`,
                      getPortfolioMediaPath(group, index),
                    )
                  }
                  className="grid h-9 w-9 place-items-center rounded-full border border-[#00a878]/40 text-[#f5f5f5]/75 outline-none ring-[#00a878] transition hover:bg-[#00a878] hover:text-[#f5f5f5] focus-visible:ring-2"
                  aria-label={`Share ${group.brand} website view ${index + 1}`}
                >
                  <Share2 className="h-4 w-4" />
                </button>
              </div>

              <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(180px,260px)] lg:items-start">
                <div className="overflow-hidden rounded-lg border border-[#00a878]/25 bg-black">
                  <img
                    src={src}
                    alt={`${group.brand} desktop website screenshot ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[5/4] w-full object-cover"
                  />
                </div>

                {mobileSrc && (
                  <div className="mx-auto w-[62%] max-w-[240px] overflow-hidden rounded-[1.7rem] border border-[#00a878]/25 bg-black p-1.5 shadow-2xl lg:w-full">
                    <img
                      src={mobileSrc}
                      alt={`${group.brand} mobile website screenshot ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="aspect-[9/21] w-full rounded-[1.35rem] object-cover"
                    />
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default WebsiteGallery;
